import { logger } from "@clockwise/client-commons/src/util/logger";
import { Button } from "@clockwise/design-system";
import { Close, Delete, OpenInFull } from "@clockwise/icons";
import { VideoLink } from "@clockwise/schema";
import { EditOutlined } from "@material-ui/icons";
import classNames from "classnames";
import React, { memo } from "react";
import toast from "react-hot-toast";
import { usePlannerContext } from "../Context";
import useEditExistingEvent from "../hooks/useEditExistingEvent";
import { JoinVideoLink } from "./JoinVideoLink";

export const Header = memo(function Header({
  onClose,
  onEdit,
  canEdit,
  canShowVideoLink = false,
  videoLink = null,
  loading = false,
  isUpcomingMeeting = false,
  deleteButton,
}: {
  onClose: () => void;
  onEdit: () => void;
  canEdit: boolean;
  canShowVideoLink?: boolean;
  videoLink?: VideoLink | null;
  isUpcomingMeeting?: boolean;
  loading?: boolean;
  deleteButton: React.ReactNode;
}) {
  return (
    <div
      className={classNames(
        "cw-text-muted cw-w-full cw-px-2 cw-py-1 cw-flex cw-h-[40px]",
        "cw-bg-neutral cw-border-t-0 cw-border-x-0 cw-border-b cw-border-solid cw-border-muted",
        "cw-overflow-hidden cw-whitespace-nowrap cw-text-ellipsis",
        {
          "cw-justify-end": loading || !canShowVideoLink || !videoLink,

          "cw-justify-between": !loading && canShowVideoLink && videoLink,
        },
      )}
    >
      {canShowVideoLink && videoLink && (
        <JoinVideoLink
          videoLink={videoLink}
          onClose={onClose}
          isUpcomingMeeting={isUpcomingMeeting}
        />
      )}
      <div className="cw-flex cw-items-center cw-gap-1">
        <Button
          startIcon={canEdit ? EditOutlined : OpenInFull}
          onClick={onEdit}
          variant="text"
          size="mini"
        />
        {deleteButton || null}
        <div className="cw-h-3 cw-w-[1px] cw-mx-1.5 cw-bg-neutral-pressed"></div>
        <Button startIcon={Close} onClick={onClose} variant="text" size="mini" />
      </div>
    </div>
  );
});

export const DeleteEvent = ({
  externalEventId,
  calendarId,
  onClose,
  canDelete,
}: {
  externalEventId: string;
  calendarId: string;
  onClose: () => void;
  canDelete: boolean;
}) => {
  const { refetchEvents } = usePlannerContext();
  const { onDeleteEvent, deletingEvent } = useEditExistingEvent(
    externalEventId,
    calendarId,
    false,
    {
      onDeletionCompleted: () => {
        if (canDelete) {
          toast.success("Event deleted.");
        } else {
          toast.success("Event removed.");
        }
        onClose();
        void refetchEvents?.();
      },
      onDeletionError: (err) => {
        toast.error("Failed to update event.");
        logger.error("unabled to delete event from event popover", {
          error: err || "unabled to delete event from event popove",
          externalEventId,
          calendarId,
        });
      },
    },
  );

  const onClick = () => {
    void onDeleteEvent();
  };

  return (
    <Button
      variant="text"
      size="mini"
      startIcon={Delete}
      disabled={deletingEvent}
      onClick={onClick}
    />
  );
};
